import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  InputBase,
  Paper,
  useScrollTrigger,
  Badge,
  Tooltip,
  useMediaQuery,
  useTheme,
  Typography,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Search,
  Notifications,
  Fullscreen,
  FullscreenExit,
} from "@mui/icons-material";
import Profile from "./Profile";
import ThemeToggle from "../ThemeToggle";
import Logo from "../../Logo";

/**
 * Modern MUI Top Navbar
 * Features:
 * - Sticky header on scroll
 * - Search bar (desktop only)
 * - Theme toggle (dark/light mode)
 * - Notifications (placeholder)
 * - Fullscreen toggle
 * - User profile dropdown (mobile only)
 */
export default function TopNavbar({ user, onMenuClick }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [searchValue, setSearchValue] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 50,
  });

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  const handleSearch = (e) => {
    setSearchValue(e.target.value);
    // Implement search functionality here
  };

  return (
    <AppBar
      position="sticky"
      elevation={trigger ? 4 : 0}
      sx={{
        bgcolor: "background.paper",
        borderBottom: trigger
          ? "1px solid"
          : "1px solid",
        borderColor: "divider",
      }}
    >
      <Toolbar sx={{ minHeight: 64 }}>
        {/* Left Section - Menu Icon & Logo (Mobile only) */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={onMenuClick}
            sx={{ 
              mr: 1, 
              display: { md: "none" },
              color: "text.primary",
            }}
          >
            <MenuIcon sx={{ fontSize: 28 }} />
          </IconButton>
          <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center" }}>
            <Logo size={40} type="icon" />
          </Box>
        </Box>

        {/* Center Section - Search */}
        <Box
          sx={{
            flexGrow: 1,
            display: { xs: "none", md: "block" },
            ml: 2,
          }}
        >
          <Paper
            component="form"
            sx={{
              p: "2px 4px",
              display: "flex",
              alignItems: "center",
              maxWidth: 600,
              mx: "auto",
            }}
          >
            <InputBase
              sx={{ ml: 1, flex: 1 }}
              placeholder="Search papers, authors..."
              value={searchValue}
              onChange={handleSearch}
              inputProps={{ "aria-label": "search" }}
            />
            <IconButton type="submit" sx={{ p: "10px" }} aria-label="search">
              <Search />
            </IconButton>
          </Paper>
        </Box>

        {/* Right Section - Actions */}
        <Box sx={{ 
          display: "flex", 
          alignItems: "center", 
          gap: 1,
          ml: "auto",
        }}>
          {/* Mobile Search Icon */}
          <IconButton
            size="large"
            aria-label="search"
            color="inherit"
            sx={{ display: { xs: "flex", md: "none" } }}
          >
            <Search />
          </IconButton>

          {/* Theme Toggle */}
          <ThemeToggle />

          {/* Notifications (Placeholder) */}
          <Tooltip title="Notifications">
            <IconButton size="large" aria-label="notifications" color="inherit">
              <Badge badgeContent={0} color="error">
                <Notifications />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Fullscreen Toggle */}
          <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
            <IconButton
              size="large"
              aria-label="fullscreen"
              color="inherit"
              onClick={toggleFullscreen}
            >
              {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Tooltip>

          {/* User Profile (Mobile only) */}
          {isMobile && <Profile user={user} />}
        </Box>
      </Toolbar>
    </AppBar>
  );
}
